/**
 * MotionSettings (BL-45) — the single source of truth for "should motion be
 * damped?". Historically it folded together two signals:
 *
 *   1. the OS `prefers-reduced-motion` media query, and
 *   2. an in-game toggle (persisted in localStorage under `aiw_reduce_motion`).
 *
 * BOTH are now intentionally NEUTRALISED for real users. The CRT / glitch arcade
 * atmosphere is identity-defining, so the user-facing toggle was removed, its
 * persisted value is no longer read (a stale `aiw_reduce_motion=1` can never
 * silently kill the effects again), and the OS signal is ignored. In production
 * `motionScale` is therefore always 1. The mechanism is kept ONLY so tests can
 * still flip it via {@link setMotionEnabled} to exercise the reduced-motion code
 * paths that remain in the renderers.
 *
 * It exposes a single `motionScale` ∈ {0, 1} that the GameLoop / renderers read:
 * 1 = full motion, 0 = reduced. Consumers decide how to apply it (a hard kill
 * for shake / full-screen flash / hit-stop, or a partial damp for the stealth
 * cloak).
 *
 * Lives in `engine/` (not `core/`, which is DOM-pure) because it reads platform
 * globals and is the lowest layer both the GameLoop and rendering can share. It
 * never touches sim state, so it cannot affect determinism, and every DOM access
 * is guarded — it returns "full motion" in non-DOM / headless (test) contexts.
 */

/** In-game toggle (true = full motion). Forced ON for real users; only tests
 *  flip it via {@link setMotionEnabled}. The persisted value is ignored. */
let motionEnabled = readPersistedToggle();

/**
 * BL-44 — the perf governor's continuous load-motion factor ∈ [floor, 1].
 * Registered via {@link setArenaLoadMotionProvider} so this leaf module stays
 * dependency-free (no import of PerfGovernor). Defaults to 1 (no damp) so the
 * arena look is byte-identical without a governor wired (tests / headless).
 *
 * CRITICAL SEPARATION: this is folded into {@link arenaMotionScale} ONLY. It is
 * NEVER folded into {@link motionScale} / {@link isReducedMotion} — accessibility
 * stays a SEPARATE, binary signal that the HUD + skill UI read, untouched by perf.
 */
let loadMotionProvider: () => number = () => 1;

/** Motion is ALWAYS on for real users: the user-facing toggle was removed and the
 *  persisted `aiw_reduce_motion` is no longer read, so it can never suppress the
 *  CRT/glitch atmosphere. Tests still force reduced motion via
 *  {@link setMotionEnabled}. */
function readPersistedToggle(): boolean {
  return true;
}

/** OS `prefers-reduced-motion` is intentionally IGNORED for the global motion
 *  signal. The glitch/CRT arcade atmosphere is identity-defining and must always
 *  play, so the OS signal is never folded into {@link motionScale}. */
function prefersReduced(): boolean {
  return false;
}

/**
 * BL-108 — the SCOPED photosensitivity carve-out signal (PT-38 #6, owner
 * decision). Reads the RAW OS `prefers-reduced-motion` directly, used ONLY by
 * the two photosensitivity-sensitive in-game surfaces:
 *   - the full-screen RED flash (ScreenEffects.flash) → zeroed
 *   - the sustained corruption MOTION (CorruptionLayer tears + veil breath) →
 *     frozen (claimed cells / sigil / static veil stay — information never lost)
 *
 * Deliberately SEPARATE from {@link motionScale} / {@link arenaMotionScale}:
 * D-212's CRT/glitch identity (boot + arena) ALWAYS plays; this signal narrows
 * the carve-out to exactly the owner's scope and never re-globalises reduced
 * motion. A `null` test override (default) falls through to the live OS query;
 * the MediaQueryList is cached so per-frame reads are cheap.
 */
let reducedMotionFlashOverride: boolean | null = null;
let rmFlashMql: MediaQueryList | null | undefined;

export function prefersReducedMotionFlash(): boolean {
  if (reducedMotionFlashOverride !== null) return reducedMotionFlashOverride;
  if (rmFlashMql === undefined) {
    try {
      rmFlashMql =
        typeof window !== "undefined" && window.matchMedia
          ? window.matchMedia("(prefers-reduced-motion: reduce)")
          : null;
    } catch {
      rmFlashMql = null;
    }
  }
  return rmFlashMql?.matches === true;
}

/** Test-only: force the BL-108 carve-out signal (`null` = use the OS query). */
export function __setReducedMotionFlashForTest(v: boolean | null): void {
  reducedMotionFlashOverride = v;
  rmFlashMql = undefined; // re-read the (mocked) matchMedia next call
}

/** Set the in-game motion toggle (true = full motion) and persist it. */
export function setMotionEnabled(enabled: boolean): void {
  motionEnabled = enabled;
  try {
    window.localStorage?.setItem("aiw_reduce_motion", enabled ? "0" : "1");
  } catch {
    /* non-DOM / storage-denied — in-memory only */
  }
}

/** Current in-game toggle value (true = full motion). */
export function isMotionEnabled(): boolean {
  return motionEnabled;
}

/** 1 = full motion; 0 = reduced (OS prefers-reduced-motion OR toggle off).
 *  Cheap to call per-entity per-frame: the OS read is cached after first use. */
export function motionScale(): number {
  return motionEnabled && !prefersReduced() ? 1 : 0;
}

/** Convenience inverse — true when motion should be damped. */
export function isReducedMotion(): boolean {
  return motionScale() === 0;
}

/** Register the perf governor's load-motion factor source (BL-44). */
export function setArenaLoadMotionProvider(fn: () => number): void {
  loadMotionProvider = fn;
}

/**
 * Effective ARENA-motion magnitude ∈ [0, 1] (BL-44). Folds the accessibility
 * hard-kill with the perf governor's continuous load factor:
 *   `isReducedMotion() ? 0 : clamp(loadMotionFactor)`
 * Read ONLY by the live-arena magnitude consumers (enemy reaction, tower recoil,
 * screen shake/flash) — NOT the HUD / skill UI, which stay on the binary
 * accessibility signal. Reduced-motion still hard-zeroes it; otherwise the
 * governor damps it gradually under load. Cheap to call per-frame.
 */
export function arenaMotionScale(): number {
  if (motionScale() === 0) return 0; // accessibility hard-kill wins
  const f = loadMotionProvider();
  return f < 0 ? 0 : f > 1 ? 1 : f;
}

/** Test-only: reset the toggle (in-memory and persisted) so suites that flip
 *  reduced-motion don't leak state. */
export function __resetMotionSettingsForTest(): void {
  motionEnabled = true;
  loadMotionProvider = () => 1;
  reducedMotionFlashOverride = null;
  rmFlashMql = undefined;
  try {
    window.localStorage?.removeItem("aiw_reduce_motion");
  } catch {
    /* non-DOM — nothing to clear */
  }
}
